import { AnhangDataType, AntragLimit } from '../model';
import { AntragFinishDto } from '../dto/antrag-finish.dto';
import { AntragUpdateDto } from '../dto/antrag-update';
import { ApiService, Guid, SKIP_MAINTENANCE_INTERCEPTOR, UploadDataForAntragsSeite } from '@ebeihilfe/core';
import { HttpContext, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ServerResponse } from 'http';
import { ValidationErrorResponse } from '../model/validation-error-response';

@Injectable({
  providedIn: 'root',
})
export class EinreichungApiService {
  private readonly API_URL = 'data/';
  private readonly api = inject(ApiService);

  public finishEinreichung(antragFinishObject: AntragFinishDto, reqOpts?: { context: HttpContext }): Observable<void> {
    return this.api.put(
      `${this.API_URL}antraege/${antragFinishObject.antragsId}`,
      {
        belegeCount: antragFinishObject.numberOfBelege,
        seitenCount: antragFinishObject.numberOfPages,
        antragsArt: antragFinishObject.typeOfAntrag,
        metadaten: antragFinishObject.encryptedMetadata,
        concurrency: antragFinishObject.antragCreationDate,
        flags: antragFinishObject.flags,
      },
      reqOpts,
    ) as Observable<void>;
  }

  public deleteAntrag(antragsId: string): Observable<void> {
    return this.api.delete(`${this.API_URL}antraege/${antragsId}`) as Observable<void>;
  }

  /**
   * Fetches the AntragsLimit data from the API.
   *
   * @return {Observable<AntragLimit>} An observable emitting the fetched limit data.
   */
  public getAntragsLimit(): Observable<AntragLimit> {
    return this.api.restGet<AntragLimit>(`${this.API_URL}antraege/info`);
  }

  public uploadAnhang(
    pagesToUpload: UploadDataForAntragsSeite & { dataType?: AnhangDataType },
  ): Observable<ValidationErrorResponse | void> {
    const formData = new FormData();
    formData.append('antragsId', pagesToUpload.antragId);
    formData.append('numberOfBelege', pagesToUpload.belegNumber);
    formData.append('numberOfSeite', pagesToUpload.seiteNumber);
    formData.append('data', pagesToUpload.pageData);
    formData.append('dataType', pagesToUpload.dataType ?? this.extractDataTypeFromDataURI(pagesToUpload.thumb));
    formData.append('thumbnail', pagesToUpload.thumb);
    formData.append('timestamp', pagesToUpload.timestamp);
    formData.append('belegtyp', pagesToUpload.belegtyp);

    return this.api.post(`${this.API_URL}antraege/${pagesToUpload.antragId}/seiten`, formData, {
      reportProgress: true,
    });
  }

  public updateBelegType(antragsId: Guid, seitenId: string, belegType: number): Observable<void> {
    const formData = new FormData();
    formData.append('belegtyp', belegType.toString());

    return this.api.patch(`${this.API_URL}antraege/${antragsId}/seiten/${seitenId}`, formData);
  }

  public updateAntrag(antragUpdateDto: AntragUpdateDto): Observable<{ concurrency: Date }> {
    return this.api.patch<{ concurrency: Date }>(`${this.API_URL}antraege/${antragUpdateDto.antragId}`, {
      concurrency: antragUpdateDto.concurrency,
      metadata: antragUpdateDto.encryptedMetadata,
    });
  }

  public deleteAnhang(pageId: string): Observable<HttpEvent<ServerResponse>> {
    return this.api.delete(`${this.API_URL}antraege/seiten/${pageId}`) as Observable<HttpEvent<ServerResponse>>;
  }

  public getSeiteFromServer(antragsId: string, seitenId: string): Observable<Blob> {
    const context = new HttpContext().set(SKIP_MAINTENANCE_INTERCEPTOR, true);

    return this.api.restGet(`data/antraege/${antragsId}/seiten/${seitenId}`, {
      responseType: 'blob',
      headers: new HttpHeaders({
        'Cache-control': 'private, max-age=31536000',
      }),
      context,
    });
  }

  private extractDataTypeFromDataURI(dataURI: string): string {
    const regex = /^data:image\/([^;]+)/;
    const match = dataURI.match(regex);
    return match ? match[1] : 'png';
  }
}
